"use client";
import * as S from "./styles.tsx";
import { IImageProps, IVideoProps } from "@/lib/types/components";
import { Button, H1, Image, P, UniversalLink } from "@template/components";
import { HeroFooterImage } from "./FooterImage";
import { IButtonProps } from "@template/components/Button";
import { ReviewsSummary } from "@template/blocks/ReviewsSummary";
import VideoComponent from "@template/blocks/HeroSection/component/VideoComponent";
import UniversalMarkdown from "../../universal-markdown.tsx";
import BasicSection from "./BasicSection/index.tsx";

interface IHeroSectionComponentProps {
  heading?: string;
  subHeading?: string;
  subHeadingCtaText?: {
    label: string;
    href?: string;
    target?: "_blank";
    title?: string;
  }[];
  buttons?: ({
    label: string;
    link: string;
    variant: "outline" | "flat" | "solid" | "light";
    target?: "_blank";
  } & Partial<IButtonProps>)[];
  badges?: {
    image: IImageProps;
    link: string;
    target?: "_blank";
  }[];
  image?: IImageProps;
  video?: IVideoProps;
  heroFooterImage?: IImageProps;
  reviews?: boolean;
  column?: boolean;
  altStyling?: boolean;
  greyBackground?: boolean;
  needBasicBlock?: boolean;
  basicBlock?: {
    heading?: string;
    image?: IImageProps;
    isParagraph?: boolean;
    content?: string;
    bullets?: { text: string }[];
    buttons?: ({
      label: string;
      link: string;
      variant: "outline" | "flat" | "solid" | "light";
      target?: "_blank";
    } & Partial<IButtonProps>)[];
    flip?: boolean;
    backgroundColor?: "grey" | "burgundy" | "white";
  };
}

const HeroSectionComponent = ({
  heading,
  subHeading,
  subHeadingCtaText,
  buttons,
  image,
  heroFooterImage,
  reviews,
  badges,
  column,
  altStyling,
  video,
  greyBackground = false,
  needBasicBlock = false,
  basicBlock,
}: IHeroSectionComponentProps) => {
  return (
    <>
      <S.HeroWrapper
        $altStyling={altStyling}
        $greyBackground={greyBackground}
      >
        <S.Container $column={column}>
          <S.HeroContainer $column={column}>
            <S.Header $altStyling={altStyling}>
              <H1>{heading}</H1>
            </S.Header>
            <S.Subheader $altStyling={altStyling}>
              <UniversalMarkdown>{subHeading}</UniversalMarkdown>
              {subHeadingCtaText && (
                <P>
                  {subHeadingCtaText.map((segment, index) =>
                    segment.href ? (
                      <UniversalLink
                        key={index}
                        href={segment.href}
                        target={segment.target}
                        title={segment.title}
                      >
                        {segment.label}
                      </UniversalLink>
                    ) : (
                      segment.label
                    )
                  )}
                </P>
              )}
            </S.Subheader>
            {buttons && buttons?.length ? (
              <S.ButtonGroup $column={column}>
                {(buttons as any).map(
                  (
                    { variant, target, link, label, children, key, ...props },
                    index: number
                  ) => (
                    <Button
                      key={index}
                      variant={variant}
                      size="lg"
                      {...props}
                      navigation={{
                        target,
                        href: link,
                      }}
                    >
                      {children || label}
                    </Button>
                  )
                )}
              </S.ButtonGroup>
            ) : null}

            {badges && badges?.length ? (
              <S.ButtonGroup>
                {badges.map((badge, index) => (
                  <UniversalLink
                    key={index}
                    href={badge?.link}
                    target={badge.target}
                  >
                    <Image
                      src={badge.image?.src}
                      alt={badge.image?.alt}
                      height={43}
                      width={148}
                      style={{
                        width: "100%",
                        height: "43px",
                      }}
                    />
                  </UniversalLink>
                ))}
              </S.ButtonGroup>
            ) : null}

            {reviews && (
              <ReviewsSummary
                column={column}
                altStyling={altStyling}
              />
            )}
          </S.HeroContainer>
          <S.HeroImageContainer>
            {video ? (
              <VideoComponent
                src={video.src}
                fallbackImageSrc={video.fallbackImageSrc}
              />
            ) : (
              image?.src && (
                <Image
                  src={image.src}
                  alt={image.alt}
                  width={600}
                  height={700}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              )
            )}
          </S.HeroImageContainer>
        </S.Container>
      </S.HeroWrapper>
      {heroFooterImage?.src && (
        <HeroFooterImage
          image={heroFooterImage}
          altStyling={altStyling}
        />
      )}
      {needBasicBlock && basicBlock && (
        <BasicSection
          altStyling={altStyling}
          greyBackground={greyBackground}
          basicBlock={basicBlock}
        />
      )}
    </>
  );
};

export default HeroSectionComponent;

export const options = [
  {
    description: "",
    displayName: "HeroSectionComponent",
    methods: [],
    props: {
      heading: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      subHeading: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      subHeadingCtaText: {
        required: false,
        flowType: {
          name: "Array",
          elements: [
            {
              name: "signature",
              type: "object",
              raw: '{ label: string; href?: string; target?: "_blank" }',
              signature: {
                properties: [
                  {
                    key: "label",
                    value: {
                      name: "string",
                      required: true,
                    },
                  },
                  {
                    key: "href",
                    value: {
                      name: "string",
                      required: false,
                    },
                  },
                  {
                    key: "target",
                    value: {
                      name: "literal",
                      value: '"_blank"',
                      required: false,
                    },
                  },
                ],
              },
            },
          ],
          raw: '{ label: string; href?: string; target?: "_blank" }[]',
        },
        description: "",
      },
      buttons: {
        required: false,
        flowType: {
          name: "Array",
          elements: [
            {
              name: "signature",
              type: "object",
              raw: '{\n  label: string;\n  link: string;\n  variant: "outline" | "flat" | "solid" | "light";\n  target?: "_blank";\n}',
              signature: {
                properties: [
                  {
                    key: "label",
                    value: {
                      name: "string",
                      required: true,
                    },
                  },
                  {
                    key: "link",
                    value: {
                      name: "string",
                      required: true,
                    },
                  },
                  {
                    key: "variant",
                    value: {
                      name: "union",
                      raw: '"outline" | "flat" | "solid" | "light"',
                      elements: [
                        {
                          name: "literal",
                          value: '"outline"',
                        },
                        {
                          name: "literal",
                          value: '"flat"',
                        },
                        {
                          name: "literal",
                          value: '"solid"',
                        },
                        {
                          name: "literal",
                          value: '"light"',
                        },
                      ],
                      required: true,
                    },
                  },
                  {
                    key: "target",
                    value: {
                      name: "literal",
                      value: '"_blank"',
                      required: false,
                    },
                  },
                ],
              },
            },
          ],
          raw: '{\n  label: string;\n  link: string;\n  variant: "outline" | "flat" | "solid" | "light";\n  target?: "_blank";\n}[]',
        },
        description: "",
      },
      badges: {
        required: false,
        flowType: {
          name: "Array",
          elements: [
            {
              name: "signature",
              type: "object",
              raw: '{\n  image: IImageProps;\n  link: string;\n  target?: "_blank";\n}',
              signature: {
                properties: [
                  {
                    key: "image",
                    value: {
                      name: "IImageProps",
                      required: true,
                    },
                  },
                  {
                    key: "link",
                    value: {
                      name: "string",
                      required: true,
                    },
                  },
                  {
                    key: "target",
                    value: {
                      name: "literal",
                      value: '"_blank"',
                      required: false,
                    },
                  },
                ],
              },
            },
          ],
          raw: '{\n  image: IImageProps;\n  link: string;\n  target?: "_blank";\n}[]',
        },
        description: "",
      },
      image: {
        required: false,
        flowType: { name: "IImageProps" },
        description: "",
      },
      video: {
        required: false,
        flowType: { name: "IVideoProps" },
        description: "",
      },
      heroFooterImage: {
        required: false,
        flowType: { name: "IImageProps" },
        description: "",
      },
      reviews: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
      },
      column: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
      },
      altStyling: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
      },
      greyBackground: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
        defaultValue: { value: "false", computed: false },
      },
    },
  },
];
